import { Fragment, PureComponent, ReactNode } from 'react';
import { CodedMedications } from '../api/fetchDrugSearch';
import { extractProperties } from '../components/common/ExtractProperties';
import { apiDrugToMedicationEntity } from '../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import ValidationItem from '../components/prescriptionPad/rightPanel/workspace/ValidationItem';
import { SNACKING_QUESTIONS } from '../components/prescriptionPad/trackSource';
import { MedicineCategory, MedicationsEntity } from '../store/types/prescription';
import { notADrugObj } from './profile/ListGenieItems';

type State<T> = {
    drug: T;
};

export type ShowDrugProps<T> = {
    drug: T;
    onChange: (d: T, matchingDrugs?: CodedMedications[]) => void;
    onSkip: () => void;
    onOtherOptionSelect: (is_otc?: boolean, med_cat?: MedicineCategory) => void;
    fCount: number;
};

interface Props<T> extends ShowDrugProps<T> {
    matchingDrugs?: CodedMedications[];
}

class ShowDrug<T extends MedicationsEntity> extends PureComponent<Props<T>, State<T>> {
    constructor(props: Props<T>) {
        super(props);
        this.state = {
            drug: this.props.drug,
        };
    }

    onSuggestedSelect = (med: CodedMedications, index: number): void => {
        const { drug } = this.state;

        const apiToMed = apiDrugToMedicationEntity(med);

        const newDrug = {
            ...apiToMed,
            ...drug,
            id: med.id,
            name: med.name,
            form: '',
            dosage_form: med.dosage?.dosage_form || '',
            df_id: med.dosage?.df_id,
            generic_id: med.generic_id,
            generic_name: med.generic_name,
            product_type: med.product_type,
            soa: med.soa || false,
            manufacturer_name: med.manufacturer_name,
            hxng_mapped: med.hxng_mapped,
            hxng_status: med.hxng_status,
            replaced_from: drug,
            replaced_track: {
                label: SNACKING_QUESTIONS,
                index,
            },
        };

        if (drug.df_id === med.dosage.df_id) {
            newDrug.dose = drug.dose;
        } else {
            newDrug.dose = apiToMed.dose;
        }

        const { onChange } = this.props;
        onChange(newDrug as T);
    };

    render(): ReactNode {
        const { matchingDrugs, onSkip, onOtherOptionSelect, fCount } = this.props;
        const { drug } = this.state;
        const { onSuggestedSelect } = this;
        const form = drug.dosage_form || drug.form;
        const properties = extractProperties.getMedicationsProperties(drug);

        const notMedicineStatus = matchingDrugs && matchingDrugs[0]?.non_medicine_status;

        const isFormValid = (drug.dosage_form || drug.form || '')?.length > 0;
        const isGenericValid = (drug.generic_name || '')?.length > 0;

        return (
            <div className="space-y-12 px-16 py-12">
                <div className="flex items-center justify-between uppercase text-12">
                    <p className="text-darwin-neutral-700">Prescribed {fCount} times</p>
                    <button
                        className="font-700 text-darwin-primary-500 uppercase px-8 py-2 border border-darwin-primary-500 rounded-4"
                        onClick={onSkip}
                    >
                        Skip
                    </button>
                </div>

                <div className="flex items-start space-x-8 bg-darwin-status-warning-100 px-12 py-8 rounded-8">
                    <div>
                        <p className="flex items-center justify-center h-26 w-26 bg-darwin-accent-medicines-pink-200 rounded-full font-700 text-darwin-accent-medicines-pink-800">
                            Rx
                        </p>
                    </div>

                    <div className="flex-1 relative top-2 space-y-6">
                        <div className="flex items-center space-x-8">
                            <p className="font-700">{drug.name}</p>
                            <svg
                                className="w-14 h-14"
                                viewBox="0 0 14 15"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M7 14.75C10.8555 14.75 14 11.6328 14 7.75C14 3.89453 10.8555 0.75 7 0.75C3.11719 0.75 0 3.89453 0 7.75C0 11.6328 3.11719 14.75 7 14.75ZM10.0898 6.46484L6.58984 9.96484C6.31641 10.2383 5.90625 10.2383 5.66016 9.96484L3.91016 8.21484C3.63672 7.96875 3.63672 7.55859 3.91016 7.3125C4.15625 7.03906 4.56641 7.03906 4.83984 7.3125L6.125 8.59766L9.16016 5.53516C9.40625 5.28906 9.81641 5.28906 10.0625 5.53516C10.3359 5.80859 10.3359 6.21875 10.0625 6.46484H10.0898Z"
                                    fill="#1B9C7D"
                                />
                            </svg>

                            {form?.trim() && (
                                <div className="pl-8">
                                    <span className="whitespace-nowrap text-darwin-neutral-700 px-4 py-2 rounded-4 border border-darwin-neutral-200 text-12 bg-white">
                                        {form}
                                    </span>
                                </div>
                            )}
                        </div>

                        <p className="truncate-overflow-1">
                            <span className="text-13 text-darwin-neutral-800">
                                {drug.generic_name}{' '}
                            </span>
                            {properties && (
                                <span className="text-13 text-darwin-neutral-800">
                                    [{properties}]
                                </span>
                            )}
                        </p>
                    </div>

                    <div className="flex space-x-16">
                        <ValidationItem isValid={isFormValid} text="Form" />
                        <ValidationItem isValid={isGenericValid} text="Generic" />
                    </div>
                </div>

                {matchingDrugs && matchingDrugs?.length > 0 && (
                    <div className="flex justify-between overflow-hidden w-full items-center">
                        <div className="overflow-hidden w-full ">
                            {matchingDrugs?.length ? (
                                <div className="space-y-10 overflow-hidden w-full relative -top-2">
                                    <p className="text-darwin-neutral-700">
                                        Select an option to fix:
                                    </p>

                                    <div
                                        className={`w-full ${
                                            matchingDrugs.length === 1
                                                ? 'flex gap-x-10'
                                                : 'space-y-12'
                                        } py-2`}
                                    >
                                        <div className="flex overflow-x-auto space-x-8">
                                            {notMedicineStatus ? (
                                                <Fragment>
                                                    <div
                                                        className="font-400 text-darwin-primary-500 whitespace-nowrap cursor-pointer rounded-8 border-darwin-primary-500 border-1 py-6 px-16"
                                                        onClick={() =>
                                                            onOtherOptionSelect(
                                                                false,
                                                                notMedicineStatus,
                                                            )
                                                        }
                                                    >
                                                        It's {notADrugObj[notMedicineStatus]}
                                                    </div>

                                                    <div
                                                        className="font-400 text-darwin-primary-500 whitespace-nowrap cursor-pointer rounded-8 border-darwin-primary-500 border-1 py-6 px-16"
                                                        onClick={() =>
                                                            onOtherOptionSelect(false, 'nad')
                                                        }
                                                    >
                                                        Not a Drug
                                                    </div>
                                                </Fragment>
                                            ) : (
                                                matchingDrugs.map((med, index) => (
                                                    <div
                                                        key={med.id}
                                                        onClick={() =>
                                                            onSuggestedSelect(med, index)
                                                        }
                                                        className="cursor-pointer rounded-8 border-darwin-primary-500 border-1 py-6 px-16"
                                                    >
                                                        <div className="flex space-x-12 items-center">
                                                            <span className="font-400 text-13 text-darwin-primary-500">
                                                                {med.name}
                                                            </span>
                                                            {med.dosage?.dosage_form && (
                                                                <span className="whitespace-nowrap text-darwin-neutral-700 px-4 py-2 rounded-4 border border-darwin-neutral-200 text-10">
                                                                    {med.dosage?.dosage_form}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {med.generic_name?.trim() && (
                                                            <span className="max-w-400 font-400 text-12 text-darwin-neutral-600 mt-2 truncate-overflow-2">
                                                                {med.generic_name}
                                                            </span>
                                                        )}
                                                        {med.manufacturer_name?.trim() && (
                                                            <span className="max-w-400 font-400 text-11 text-darwin-neutral-500 mt-2 truncate-overflow-2">
                                                                Mfr: {med.manufacturer_name}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>

                                        <div className="flex space-x-10">
                                            {!notMedicineStatus && (
                                                <div
                                                    className="flex items-center whitespace-nowrap font-400 text-darwin-primary-500 cursor-pointer rounded-8 border border-darwin-primary-500 py-4 px-16"
                                                    onClick={() => onOtherOptionSelect(true)}
                                                >
                                                    Mark as OTC
                                                </div>
                                            )}
                                            <div
                                                className="flex items-center whitespace-nowrap font-400 text-darwin-status-warning-500 cursor-pointer rounded-8 border border-darwin-status-warning-500 bg-darwin-status-warning-100 py-4 px-16"
                                                onClick={() => onOtherOptionSelect()}
                                            >
                                                None of these
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default ShowDrug;
